import AppRoutes from "./Routes/AppRoutes";
import { ConfigProvider, message, Spin } from "antd";
import "./App.css";
import { decryptText, encryptText } from "./Utils/Encryption";
import updateUserStates, {
  getRefreshToken,
  handleLogout,
} from "./Utils/UpdateUsersState";
import { createContext, useEffect, useState } from "react";
import { checkApiKeyValidity, refresh } from "./API/auth";
import axios from "axios";
import { ThemeProvider } from "./Contexts/ThemeProvider";

const UserContext = createContext();

function App() {
  const [userData, setUserData] = useState({
    role: "",
    id: "",
    email: "",
    name: "",
    profile_picture: "",
  });
  const [userToken, setUserToken] = useState({ access: "", refresh: "" });
  const [userPages, setUserPages] = useState([]);
  const [userApiKeyStatus, setUserApiKeyStatus] = useState({
    status: "",
    message: "",
  });
  const [loading, setLoading] = useState(true);

  const checkValidation = async () => {
    try {
      const response = await checkApiKeyValidity();
      setUserApiKeyStatus({
        status: response.data.status,
        message: response.data.message,
      });
    } catch (error) {
      message.error("Failed To Check Api Key Status");
    }
  };

  const refreshAccessToken = async (token) => {
    let myRefreshToken = token?.refresh;
    if (!myRefreshToken) {
      myRefreshToken = getRefreshToken();
    }
    const res = await refresh(myRefreshToken);
    if (res?.status === 200) {
      await checkValidation();
      const localStorageItems = localStorage;
      if (localStorageItems.length !== 0) {
        for (let key in localStorageItems) {
          if (localStorageItems[key] && decryptText(key) === "token") {
            localStorage.removeItem(key);
          }
        }
      }
      localStorage.setItem(
        encryptText("token"),
        encryptText(
          JSON.stringify({
            access: res?.data?.access,
            refresh: res?.data?.refresh,
          })
        )
      );
      updateUserStates(setUserData, setUserToken);
      setUserPages(res?.data?.pages);
      return res?.data?.access;
    } else {
      handleLogout(
        setUserData,
        setUserToken,
        setUserPages,
        setUserApiKeyStatus
      );
      window.location.href = "/login";
      return null;
    }
  };

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (
        error?.response?.status === 401 &&
        error.config.url !== `${process.env.REACT_APP_BACKEND_URL}token/` &&
        error.config.url !==
          `${process.env.REACT_APP_BACKEND_URL}token/refresh/`
      ) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          error.config.headers["Authorization"] = "Bearer " + newToken;
          return axios(error.config);
        } else {
          handleLogout(
            setUserData,
            setUserToken,
            setUserPages,
            setUserApiKeyStatus
          );
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const initializeUserState = async () => {
      setLoading(true);
      const updatedStates = updateUserStates(setUserData, setUserToken);
      if (
        updatedStates?.token?.access &&
        updatedStates?.token?.refresh &&
        loading
      ) {
        await refreshAccessToken(updatedStates.token);
      }
      setLoading(false);
    };

    if (
      userData.role === "" &&
      userToken.access === "" &&
      userToken.refresh === "" &&
      loading
    ) {
      initializeUserState();
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#155DFC",
          boxShadow: "none",
        },
        components: {
          Input: {
            paddingBlock: 8,
            paddingInline: 12,
          },
        },
      }}
    >
      <div className="App">
        <UserContext.Provider
          value={{
            userData,
            setUserData,
            userToken,
            setUserToken,
            userPages,
            setUserPages,
            userApiKeyStatus,
            setUserApiKeyStatus,
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                width: "100%",
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <ThemeProvider>
              <AppRoutes />
            </ThemeProvider>
          )}
        </UserContext.Provider>
      </div>
    </ConfigProvider>
  );
}

export default App;
export { UserContext };
